import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Trash2 } from "lucide-react";
import type { TaskComment } from "@/features/tasks/hooks/useTaskDetail";
import { Avatar } from "@/shared/components/ui/Avatar";
import { Spinner } from "@/shared/components/ui/Spinner";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import { useAuth } from "@/features/auth/hooks/useAuth";

interface TaskCommentsProps {
  comments: TaskComment[];
  loading: boolean;
  onAddComment: (text: string) => Promise<void>;
  onDeleteComment: (id: string) => Promise<void>;
}

export const TaskComments: React.FC<TaskCommentsProps> = ({
  comments,
  loading,
  onAddComment,
  onDeleteComment,
}) => {
  const { t } = useTranslation();
  const { fmtDateTime } = useFormatDate();
  const { userData } = useAuth();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await onAddComment(text.trim());
      setText("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await onDeleteComment(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">
          {t("comments.no_comments", { defaultValue: "No comments yet" })}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {comments.map((comment) => {
            const parts = comment.user_name.split(" ");
            const isOwn =
              String(userData?.id) === comment.created_by;
            return (
              <div key={comment.id} className="flex gap-2.5 group">
                <Avatar
                  firstName={parts[0]}
                  lastName={parts[1]}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {comment.user_name}
                    </span>
                    <span className="text-xs text-gray-400">
                      {fmtDateTime(comment.created_at)}
                    </span>
                    {isOwn && (
                      <button
                        onClick={() => handleDelete(comment.id)}
                        disabled={deletingId === comment.id}
                        className="ml-auto opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-red-500 transition-all"
                      >
                        {deletingId === comment.id ? (
                          <Spinner />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">
                    {comment.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add comment form */}
      <form onSubmit={handleSubmit} className="flex gap-2 pt-2 border-t border-gray-100">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("comments.placeholder", { defaultValue: "Add a comment..." })}
          rows={2}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              handleSubmit(e as any);
            }
          }}
        />
        <button
          type="submit"
          disabled={!text.trim() || submitting}
          className="self-end px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? <Spinner /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};
